<template>
    <!-- Competitors to add to the weekly competitive analysis (API routes/competitive.ts /competitors).
         Same readers as the report. The weekly job researches open ones and marks the edition that covered them. -->
    <section class="cr" aria-labelledby="cr-title">
        <div class="cr-head">
            <div>
                <h3 id="cr-title">Add a competitor</h3>
                <p>Found a company we should be watching? Add it here and the next weekly edition researches it and adds it to the report.</p>
            </div>
            <button v-if="!open" type="button" class="ai-btn cr-open" @click="openForm">Add a competitor</button>
        </div>

        <form v-if="open" class="cr-form" novalidate @submit.prevent="submit">
            <div class="cr-field">
                <label for="cr-name">Competitor name</label>
                <input id="cr-name" ref="name" v-model="form.name" type="text" maxlength="120" autocomplete="off" required placeholder="e.g. Acme Ops" />
            </div>
            <div class="cr-field">
                <label for="cr-website">Website <span class="cr-opt">(optional)</span></label>
                <input id="cr-website" v-model="form.website" type="text" inputmode="url" maxlength="300" autocomplete="off" placeholder="https://" />
            </div>
            <div class="cr-field cr-wide">
                <label for="cr-note">Why, or where you found them <span class="cr-opt">(optional)</span></label>
                <textarea id="cr-note" v-model="form.note" rows="2" maxlength="1000" placeholder="e.g. A dealer mentioned them; they sell SOP software to manufacturers."></textarea>
            </div>
            <div v-if="formError" class="cr-error cr-wide" role="alert">{{ formError }}</div>
            <div class="cr-actions cr-wide">
                <button type="button" class="cr-cancel" :disabled="saving" @click="closeForm">Cancel</button>
                <button type="submit" class="ai-btn" :disabled="saving || !form.name.trim()">{{ saving ? 'Adding…' : 'Add competitor' }}</button>
            </div>
        </form>
        <div v-if="notice" class="cr-notice" role="status">{{ notice }}</div>

        <div v-if="loadError" class="cr-error" role="alert">{{ loadError }} <button type="button" class="cr-link" @click="load">Try again</button></div>
        <ul v-else-if="requests.length" class="cr-list" aria-label="Competitors added">
            <li v-for="r in requests" :key="r.id" class="cr-item">
                <div class="cr-item-main">
                    <span class="cr-name"><a v-if="safeUrl(r.website)" :href="safeUrl(r.website)" target="_blank" rel="noopener">{{ r.name }}</a><template v-else>{{ r.name }}</template></span>
                    <span class="cr-status" :class="'cr-st-' + r.status">
                        <template v-if="r.status == 'covered'">Covered in the <a v-if="r.coveredEdition" href="#" @click.prevent="$emit('open-edition', r.coveredEdition)">{{ niceDate(r.coveredEdition) }}</a><template v-else>latest</template> edition</template>
                        <template v-else-if="r.status == 'declined'">Not added{{ r.statusNote ? ': ' + r.statusNote : '' }}</template>
                        <template v-else>Requested · will be covered in the next edition</template>
                    </span>
                </div>
                <div v-if="r.note" class="cr-note">{{ r.note }}</div>
                <div class="cr-meta">
                    Added{{ r.requestedByName ? ' by ' + r.requestedByName : '' }} on {{ shortDate(r.createdAt) }}
                    <button v-if="r.mine" type="button" class="cr-link" :disabled="removing == r.id" @click="remove(r)">{{ removing == r.id ? 'Removing…' : 'Remove' }}</button>
                </div>
            </li>
        </ul>
    </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from 'axios';
import { niceDate, sourceUrl } from '@/competitiveText';

export default defineComponent({
    name: 'CompetitorRequests',
    props: { apiUrl: { type: String, required: true } },
    emits: [ 'open-edition' ],
    data() {
        return {
            requests: [] as any[],
            open: false,
            saving: false,
            removing: 0,
            form: { name: '', website: '', note: '' },
            formError: '',
            notice: '',
            loadError: ''
        };
    },
    created() { this.load(); },
    methods: {
        niceDate,
        safeUrl(v: string) { return v ? sourceUrl(v) : undefined; },
        shortDate(v: string) { const d = new Date(v); return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); },
        headers() { return { 'X-Competitive-Request': '1' }; },
        // Also called by the page after the report chat notes a competitor
        async load() {
            try {
                this.requests = (await axios.get(`${this.apiUrl}/api/competitive/competitors`)).data;
                this.loadError = '';
            } catch {
                this.loadError = 'The list of added competitors could not be loaded.';
            }
        },
        openForm() {
            this.open = true;
            this.notice = '';
            this.$nextTick(() => (this.$refs.name as HTMLInputElement)?.focus());
        },
        closeForm() {
            this.open = false;
            this.formError = '';
            this.form = { name: '', website: '', note: '' };
        },
        async submit() {
            if(this.saving) return;
            if(!this.form.name.trim()) { this.formError = 'Enter the competitor\'s name.'; return; }
            this.saving = true;
            this.formError = '';
            try {
                const resp = await axios.post(`${this.apiUrl}/api/competitive/competitors`, this.form, { headers: this.headers() });
                const r = resp.data.request;
                this.notice = resp.data.duplicate
                    ? (r.status == 'covered' ? `${r.name} is already on the list. It was covered in the ${niceDate(r.coveredEdition)} edition.` : `${r.name} is already on the list for the next edition.`)
                    : `Added. The next weekly edition will research ${r.name}.`;
                this.closeForm();
                await this.load();
            } catch(e: any) {
                const d = e?.response?.data;
                this.formError = typeof d == 'string' && d && d.length < 300 ? d : 'That couldn\'t be saved. Please try again.';
            }
            this.saving = false;
        },
        async remove(r: any) {
            if(this.removing || !window.confirm(`Remove ${r.name} from the list?`)) return;
            this.removing = r.id;
            this.notice = '';
            try {
                await axios.delete(`${this.apiUrl}/api/competitive/competitors/${r.id}`, { headers: this.headers() });
                this.requests = this.requests.filter((x: any) => x.id !== r.id);
            } catch {
                this.notice = `${r.name} couldn't be removed. Please try again.`;
            }
            this.removing = 0;
        }
    }
});
</script>

<style scoped>
.cr { margin: 14px 0 6px; background: #fff; border: 1px solid #ecdccd; border-radius: 14px; padding: 16px 18px; display: grid; gap: 12px; min-width: 0; }
.cr-head { display: flex; gap: 12px; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; }
.cr-head h3 { margin: 0; font-size: 17px; font-weight: 800; color: #2a2a2a; }
.cr-head p { margin: 2px 0 0; font-size: 13.5px; color: #8b8682; max-width: 66ch; }
.cr-open { white-space: nowrap; }
.cr-form { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 14px; }
.cr-field { display: grid; gap: 4px; min-width: 0; }
.cr-wide { grid-column: 1 / -1; }
.cr-field label { font-size: 12px; font-weight: 700; color: #5c4636; }
.cr-opt { font-weight: 400; color: #8b8682; }
.cr-field input, .cr-field textarea { width: 100%; box-sizing: border-box; border: 1px solid #e3dcd5; border-radius: 10px; padding: 9px 12px; font: inherit; font-size: 15px; margin: 0; height: auto; }
.cr-field textarea { min-height: 56px; resize: vertical; }
.cr-field input:focus, .cr-field textarea:focus { outline: 2px solid #ffb27a; outline-offset: 1px; border-color: #ff6e00; }
.cr-actions { display: flex; justify-content: flex-end; align-items: center; gap: 10px; }
.cr-cancel, .cr-link { background: none; border: 0; color: #8b8682; cursor: pointer; font: inherit; font-size: 14px; text-decoration: underline; padding: 0; }
.cr-link { font-size: 13px; margin-left: 8px; }
.cr-error { color: #a52c26; font-size: 14px; }
.cr-notice { background: #e5f3ec; color: #1f5f3e; border-radius: 8px; padding: 8px 12px; font-size: 14px; }
.cr-list { list-style: none; padding: 0; margin: 0; display: grid; gap: 8px; }
.cr-item { list-style: none; border-top: 1px solid #f0ebe6; padding-top: 8px; max-width: none; }
.cr-item:first-child { border-top: 0; padding-top: 0; }
.cr-item-main { display: flex; flex-wrap: wrap; gap: 4px 10px; align-items: baseline; }
.cr-name { font-weight: 700; overflow-wrap: anywhere; }
.cr-name a { color: #b84f00; }
.cr-status { font-size: 12.5px; border-radius: 4px; padding: 1px 7px; background: #fbf0d9; color: #8a5a00; overflow-wrap: anywhere; }
.cr-st-covered { background: #e5f3ec; color: #1f7a4d; }
.cr-st-covered a { color: inherit; }
.cr-st-declined { background: #efeeec; color: #66625e; }
.cr-note { font-size: 14px; color: #4b4642; margin-top: 3px; white-space: pre-wrap; overflow-wrap: anywhere; }
.cr-meta { font-size: 12.5px; color: #8b8682; margin-top: 2px; }
@media (max-width: 600px) {
    .cr-form { grid-template-columns: 1fr; }
    .cr-open { width: 100%; }
}
</style>
